import { Component, ErrorInfo, ReactNode } from 'react';

import { Button } from 'fluxo-ui';
import { DashboardIcon, RefreshIcon } from 'fluxo-ui/icons';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
        errorInfo: null,
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error, errorInfo: null };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('Uncaught error:', error, errorInfo);
        this.setState({
            error,
            errorInfo,
        });
    }

    private handleReset = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
        window.location.href = '/';
    };

    private handleReload = () => {
        window.location.reload();
    };

    public render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 px-4">
                    <div className="max-w-2xl w-full">
                        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 p-8">
                            <div className="text-center mb-6">
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 dark:bg-red-900 rounded-full mb-4">
                                    <svg
                                        className="w-8 h-8 text-red-600 dark:text-red-400"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                        />
                                    </svg>
                                </div>
                                <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">Something went wrong</h1>
                                <p className="text-slate-600 dark:text-slate-400">
                                    We apologize for the inconvenience. An unexpected error has occurred.
                                </p>
                            </div>

                            {this.state.error && import.meta.env.DEV && (
                                <div className="mb-6 p-4 bg-slate-100 dark:bg-slate-700 rounded-lg">
                                    <p className="text-sm font-mono text-red-600 dark:text-red-400 break-all">
                                        {this.state.error.toString()}
                                    </p>
                                    {this.state.errorInfo && (
                                        <details className="mt-2">
                                            <summary className="text-sm text-slate-600 dark:text-slate-400 cursor-pointer">
                                                Stack trace
                                            </summary>
                                            <pre className="mt-2 text-xs text-slate-600 dark:text-slate-400 overflow-auto max-h-64">
                                                {this.state.errorInfo.componentStack}
                                            </pre>
                                        </details>
                                    )}
                                </div>
                            )}

                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <Button
                                    label="Reload Page"
                                    variant="default"
                                    layout="outlined"
                                    leftIcon={RefreshIcon}
                                    onClick={this.handleReload}
                                />
                                <Button label="Go to Dashboard" variant="primary" leftIcon={DashboardIcon} onClick={this.handleReset} />
                            </div>

                            <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700 text-center">
                                <p className="text-sm text-slate-500 dark:text-slate-400">
                                    If this problem persists, please{' '}
                                    <a href="mailto:support@testam.com" className="text-teal-600 hover:text-teal-700 dark:text-teal-400">
                                        contact support
                                    </a>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
